import type { Person } from '@/store/people/types';
import { Title } from './title';
import {
  useProfileMilestones,
  useDeleteMilestone,
} from '@/store/milestones/hooks';
import {
  Button,
  CircularProgress,
  Dropdown,
  DropdownItem,
  DropdownMenu,
  DropdownTrigger,
} from '@/components/ui';
import { Empty } from '../common/empty';
import MilestoneCard from '../dashboard/milestones/MilestoneCard';
import { StandardIcon } from '../common/icon';
import { useMilestoneModal } from '../milestones/context';
import type { Milestone } from '@/store/milestones/types';
import type { PeopleAccess } from '@/shared/types/policy';

export function Milestones({
  person,
  access,
}: {
  person: Person;
  access: PeopleAccess;
}) {
  const {
    milestones,
    loaded,
    loading,
    addMilestone,
    updateMilestone,
    removeMilestone,
  } = useProfileMilestones(person.id);
  const { deleteMilestone: doDeleteMilestone } = useDeleteMilestone();
  const {
    setCurrentPerson,
    setCurrentMilestone,
    setOpen: setModalIsOpen,
    setOnCreate,
    setOnUpdate,
  } = useMilestoneModal();

  const allowWrites = access?.people_edit === 'allow';

  if (loading || !loaded) {
    return (
      <div className='flex w-full flex-col items-center pt-8'>
        <CircularProgress size='lg' />
      </div>
    );
  }

  const onAddClick = () => {
    setCurrentPerson(person);
    setOnCreate({ callback: addMilestone });
    setModalIsOpen(true);
  };

  const editMilestone = (item: Milestone) => {
    setCurrentPerson(person);
    setCurrentMilestone(item);
    setOnUpdate({ callback: updateMilestone });
    setModalIsOpen(true);
  };

  const deleteMilestone = async (item: Milestone) => {
    await doDeleteMilestone(item);
    removeMilestone(item);
  };

  if (!milestones.length) {
    return (
      <div>
        {allowWrites && (
          <div className='flex w-full flex-row justify-end'>
            <Button color='primary' onPress={onAddClick}>
              <StandardIcon id='mdi:plus' color='text-primary-900' />
              Add
            </Button>
          </div>
        )}
        <Empty icon='mdi:event-outline' description='No milestones defined' />
      </div>
    );
  }

  return (
    <>
      <Title>
        <div className='flex flex-row items-center'>
          <span className='flex-grow'>Milestones</span>
          {allowWrites && (
            <Button color='primary' onPress={onAddClick}>
              <StandardIcon id='mdi:plus' color='text-primary-900' />
              Add
            </Button>
          )}
        </div>
      </Title>
      <div className='mt-4'>
        {milestones.map((item) => {
          if (!item) {
            debugger;
          }

          return (
            <div className='flex flex-row items-center' key={item.id}>
              <MilestoneCard data={item.data} drawer className='flex-grow' />
              {allowWrites && (
                <Dropdown>
                  <DropdownTrigger>
                    <Button isIconOnly variant='light' size='sm'>
                      <StandardIcon id='mdi:dots-vertical' />
                    </Button>
                  </DropdownTrigger>
                  <DropdownMenu>
                    <DropdownItem
                      key='edit'
                      onPress={() => editMilestone(item)}
                    >
                      Edit
                    </DropdownItem>
                    <DropdownItem
                      key='delete'
                      onPress={() => deleteMilestone(item)}
                    >
                      Delete
                    </DropdownItem>
                  </DropdownMenu>
                </Dropdown>
              )}
            </div>
          );
        })}
      </div>
    </>
  );
}
